import { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import type React from "react";
import { SearchInput } from "../components/SearchInput";
import { ColorPickerBox } from "../components/ColorPickerBox";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useDataLoader } from "../hooks/useDataLoader";
import { loadHTMLData } from "../utils/dataLoader";
import type { HTMLColor, UnicodeSymbol } from "../types/data";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { routeSeo } from "../content/seoRoutes";

export function HtmlColorsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || "colors";
  const urlSearchTerm = searchParams.get('q') || "";

  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchTerm);
  const [view, setView] = useState<"picker" | "grid" | "lists">("picker");
  const { data, loading, error } = useDataLoader(loadHTMLData);
  const htmlColors: HTMLColor[] = data?.colors ?? [];
  const unicodeSymbols: UnicodeSymbol[] = data?.symbols ?? [];

  // Debounce search term to avoid lagging while typing
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const trimmed = searchTerm.trim();
        if (trimmed) next.set('q', trimmed);
        else next.delete('q');
        return next;
      }, { replace: true });
    }, 200);
    return () => clearTimeout(id);
  }, [searchTerm, setSearchParams]);

  // Sync URL search parameters to state on mount/back navigation
  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setDebouncedSearch(urlSearchTerm);
  }, [urlSearchTerm]);

  const handleTabChange = useCallback((tab: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab && tab !== 'colors') next.set('tab', tab);
      else next.delete('tab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Color picker UI moved into <ColorPickerBox />

  // --- Hash-based routing for the view ---
  useEffect(() => {
    const hash = window.location.hash.replace('#', '').toLowerCase();
    const map: Record<string, typeof view> = {
      'color-picker': 'picker',
      'all-colors': 'grid',
      'lists': 'lists',
    };
    if (map[hash]) setView(map[hash]);

    const onHashChange = () => {
      const h = window.location.hash.replace('#', '').toLowerCase();
      if (map[h]) setView(map[h]);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    const reverse: Record<typeof view, string> = {
      picker: 'color-picker',
      grid: 'all-colors',
      lists: 'lists',
    };
    const target = `#${reverse[view]}`;
    if (window.location.hash !== target) {
      window.location.hash = target;
    }
  }, [view]);

  // Generate a comprehensive HSL palette programmatically for the "All Colors" grid
  // Hue every 10 degrees, saturation 100%, lightness steps to cover dark-mid-light
  const allColors = useMemo(() => {
    const items: { hex: string; name: string }[] = [];
    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    const hslToHex = (h: number, s: number, l: number) => {
      s /= 100; l /= 100;
      const k = (n: number) => (n + h / 30) % 12;
      const a = s * Math.min(l, 1 - l);
      const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
      const r = Math.round(255 * f(0));
      const g = Math.round(255 * f(8));
      const b = Math.round(255 * f(4));
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    };
    const lightnessSteps = [20, 35, 50, 65, 80];
    for (let l of lightnessSteps) {
      for (let h = 0; h < 360; h += 10) {
        const hex = hslToHex(h, 100, l);
        items.push({ hex, name: `HSL(${h},100%,${l}%)` });
      }
    }
    // Add grayscale row
    for (let l = 0; l <= 100; l += 10) {
      const v = toHex(Math.round((l / 100) * 255));
      items.push({ hex: `#${v}${v}${v}`.toUpperCase(), name: `Gray ${l}%` });
    }
    return items;
  }, []);

  const filteredColors = useMemo(() => {
    if (!debouncedSearch) return htmlColors;

    const term = debouncedSearch.toLowerCase();
    return htmlColors.filter(
      (color) =>
        color.name.toLowerCase().includes(term) ||
        color.hex.toLowerCase().includes(term) ||
        color.category.toLowerCase().includes(term)
    );
  }, [debouncedSearch, htmlColors]);

  const filteredSymbols = useMemo(() => {
    if (!debouncedSearch) return unicodeSymbols;

    const term = debouncedSearch.toLowerCase();
    return unicodeSymbols.filter(
      (symbol) =>
        symbol.name.toLowerCase().includes(term) ||
        symbol.unicode.toLowerCase().includes(term) ||
        symbol.category.toLowerCase().includes(term)
    );
  }, [debouncedSearch, unicodeSymbols]);

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": "HTML Colors and Unicode Symbols Database",
    "description": "Complete database and color picker for HTML colors (HEX, RGB) and Unicode symbols categorized for developer references",
    "keywords": ["HTML colors", "Unicode symbols", "color picker", "HEX code finder", "Unicode character database"],
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "creator": {
      "@type": "Organization",
      "name": "Indian Codes Hub"
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${window.location.origin}/html-colors`,
      "encodingFormat": "text/html"
    }
  };

  return (
    <>
      <SEO
        {...routeSeo('html-colors')}
        keywords="HTML colors, Unicode symbols, color picker, HEX codes, RGB colors, Unicode characters reference"
        structuredData={structuredData}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">HTML Colors & Unicode Symbols</h1>
        <p className="text-sm sm:text-base text-gray-600">Pick, browse, or search colors and symbols</p>
      </div>

      {loading && (
        <div className="text-center py-8 text-gray-500">Loading data...</div>
      )}
      {error && (
        <div className="text-center py-8 text-red-600">Failed to load data. Please try again later.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="space-y-2">
          <div className="hidden lg:block text-xs uppercase tracking-wide text-gray-500 mb-1">Sections</div>
          <button
            className={`w-full text-left px-3 py-2 rounded border ${view === "picker" ? "bg-gray-100 border-gray-300" : "border-transparent hover:bg-gray-50"}`}
            onClick={() => setView("picker")}
          >
            Color Picker
          </button>
          <button
            className={`w-full text-left px-3 py-2 rounded border ${view === "grid" ? "bg-gray-100 border-gray-300" : "border-transparent hover:bg-gray-50"}`}
            onClick={() => setView("grid")}
          >
            All Colors Grid
          </button>
          <button
            className={`w-full text-left px-3 py-2 rounded border ${view === "lists" ? "bg-gray-100 border-gray-300" : "border-transparent hover:bg-gray-50"}`}
            onClick={() => setView("lists")}
          >
            Lists (HTML Colors & Symbols)
          </button>
        </aside>

        {/* Content */}
        <section>
          {view === "lists" && (
            <div className="mb-6">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search colors, symbols, or codes..."
              />
            </div>
          )}

          {view === "picker" && (
            <div className="">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Color Picker</h2>
              <ColorPickerBox namedColors={htmlColors.map(c => ({ name: c.name, hex: c.hex }))} />
            </div>
          )}

          {view === "grid" && (
            <div className="">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-gray-900">All Colors</h2>
                <p className="text-xs text-gray-500">Hover to preview • Click to copy HEX</p>
              </div>
              <div className="grid grid-cols-10 sm:grid-cols-12 md:grid-cols-18 lg:grid-cols-24 gap-1">
                {allColors.map((c, idx) => (
                  <button
                    key={`${c.hex}-${idx}`}
                    className="group relative aspect-square rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-sky-500"
                    style={{ backgroundColor: c.hex }}
                    title={`${c.name} • ${c.hex}`}
                    onClick={() => {
                      navigator.clipboard.writeText(c.hex).then(() => {
                        toast.success(`${c.hex} copied`);
                      }).catch(() => toast.error("Copy failed"));
                    }}
                  >
                    <span className="pointer-events-none absolute inset-x-0 bottom-0 text-[10px] sm:text-[11px] leading-tight text-white/90 mix-blend-difference px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {c.hex}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {view === "lists" && (
            <>
             <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="colors">HTML Colors</TabsTrigger>
          <TabsTrigger value="symbols">Unicode Symbols</TabsTrigger>
        </TabsList>

        <TabsContent value="colors">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredColors.map((color) => (
              <Card key={color.name} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <span className="font-medium">{color.name}</span>
                    <div 
                      className="w-8 h-8 rounded border border-gray-300"
                      style={{ backgroundColor: color.hex }}
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-gray-500">Hex</p>
                      <p className="font-mono text-sm">{color.hex}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">RGB</p>
                      <p className="font-mono text-sm">{color.rgb}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="symbols">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSymbols.map((symbol, index) => (
              <Card key={`${symbol.unicode}-${index}`} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-3xl">{symbol.symbol}</span>
                    <Badge variant="outline">{symbol.category}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium">{symbol.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Unicode</p>
                      <p className="font-mono text-sm">{symbol.unicode}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
            </Tabs>

            {(filteredColors.length === 0 && filteredSymbols.length === 0) && (
              <div className="text-center py-12">
                <p className="text-gray-500">No colors or symbols found matching your search.</p>
              </div>
            )}
            </>
          )}
        </section>
      </div>
    </div>
  </>
  );
}